import { useAsync } from 'react-use';

import {
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { NotFoundError } from '@backstage/errors';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Resolves the names of the supervisory-org groups the signed-in user owns.
 *
 * Used to narrow a DaaS catalog query to entities owned by one of the caller's
 * own teams (`spec.owner`). Returns an empty list - never every group - when
 * the caller owns nothing resolvable.
 */
export const useGetUserGroups = () => {
  const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    const { ownershipEntityRefs } = await identityApi.getBackstageIdentity();
    if (!ownershipEntityRefs || ownershipEntityRefs.length === 0) {
      errorApi.post(new NotFoundError('No ownership entity refs found'));
      return [];
    }

    const groupRefs = ownershipEntityRefs.filter(ref =>
      ref.startsWith('group:'),
    );

    if (groupRefs.length === 0) {
      return [];
    }

    const groupFields = ['metadata.name'];

    const { items: userGroups } = await catalogApi.getEntitiesByRefs({
      entityRefs: groupRefs,
      filter: {
        kind: 'Group',
        'spec.type': 'supervisory-org',
      },
      fields: groupFields,
    });

    if (userGroups.length === 0) {
      return [];
    }

    return userGroups
      .filter(group => group?.metadata?.name)
      .map(group => group!.metadata.name);
  });

  return { value: entities, loading, error };
};
